
"use client";

import * as React from "react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

interface DateInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  onDateChange?: (date: Date | null) => void;
}

const DateInput = React.forwardRef<HTMLInputElement, DateInputProps>(
  ({ className, onDateChange, onChange, value, ...props }, ref) => {
    const [inputValue, setInputValue] = React.useState<string>(value as string || "");

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      let newValue = e.target.value.replace(/[^\d]/g, "").slice(0, 8);
      let formattedValue = "";

      if (newValue.length > 0) {
        formattedValue = newValue.slice(0, 2);
        
        if (newValue.length > 2) {
          formattedValue += "-" + newValue.slice(2, 4);
        }
        
        if (newValue.length > 4) {
          formattedValue += "-" + newValue.slice(4, 8);
        }
      }

      setInputValue(formattedValue);

      // Call original onChange if provided
      if (onChange) {
        const syntheticEvent = {
          ...e,
          target: {
            ...e.target,
            value: formattedValue
          }
        };
        onChange(syntheticEvent);
      }

      // Parse and validate date
      if (formattedValue.length === 10) {
        const [day, month, year] = formattedValue.split("-").map(Number);
        
        // Check if date is valid
        const date = new Date(year, month - 1, day);
        const isValidDate = 
          date.getFullYear() === year && 
          date.getMonth() === month - 1 && 
          date.getDate() === day;
        
        if (isValidDate && onDateChange) {
          onDateChange(date);
        } else if (onDateChange) {
          onDateChange(null);
        }
      } else if (onDateChange) {
        onDateChange(null);
      }
    };

    return (
      <Input
        type="text"
        ref={ref}
        value={inputValue}
        onChange={handleChange}
        placeholder="DD-MM-YYYY"
        maxLength={10}
        className={cn("", className)}
        {...props}
      />
    );
  }
);

DateInput.displayName = "DateInput";

export { DateInput };
